'use client'

// Client component: the CTA button needs an onClick (mapped from the
// declarative `action` prop below), and our parent is a SERVER page —
// functions must originate inside the client boundary, so the boundary
// starts here. All props this component accepts are serializable.
import clsx from 'clsx'
import { ReactNode } from 'react'
import Button from '../Button/Button'
import Header from '../Header/Header'
import Icon, { IconProps } from '../Icon/Icon'

import PageSection from '../PageSection/PageSection'

import { findYoutubeId } from 'lib/utilities/string'
import { ImageStandAlone as Image, ImageStandAloneProps } from '../Image/Image'
import YouTubeFacade from '../YouTubeFacade/YouTubeFacade'

import { redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'
import GolfBagSizes from 'lib/components/GolfBagSizes/GolfBagSizes'
import LuggageSizes from 'lib/components/LuggageSizes/LuggageSizes'
import WinterGearSizes from 'lib/components/WinterGearSizes/WinterGearSizes'
import WinterLuggageSizes from 'lib/components/WinterLuggageSizes/WinterLuggageSizes'

// Buttons are declared in per-brand page.content files, which are server
// modules — static export forbids passing functions across the server->client
// boundary, so behavior is named declaratively and mapped to a handler here
// (inside the client tree).
type FiftyFiftyButtonAction = 'booking-widget-redirect'

const BUTTON_ACTIONS: Record<FiftyFiftyButtonAction, () => void> = {
  // Escapes the iframe embed via window.top.location.assign and respects the
  // booking-widget-target flag, same as the form START button (GSE-11866).
  'booking-widget-redirect': () => redirectFromBookingWidget(''),
}

interface FiftyFiftyButton {
  text: string
  link?: {
    href: string
  }
  action?: FiftyFiftyButtonAction
}

type FiftyFiftyImage = Pick<ImageStandAloneProps, 'src' | 'alt'>

export interface FiftyFiftyProps {
  /**
   * Background color
   */
  backgroundColor: 'white' | 'grey'
  /**
   * Image URL and optional alt text
   */
  image?: FiftyFiftyImage
  /**
   * Optional YouTube video URL. When provided, renders a video instead of an image.
   */
  videoUrl?: string
  /**
   * Component layout
   */
  layout: 'image-left' | 'image-right'
  /**
   * Optional eyebrow icon and text. Ampersand logo will show if left blank
   */
  eyebrow?: {
    icon: IconProps
    text: string
  }
  /**
   * Whether to render the eyebrow section at all
   * @default true
   */
  showEyebrow?: boolean
  /**
   * Copy for header
   */
  header: string | ReactNode
  /**
   * Copy for subheader
   */
  subHeader?: string | ReactNode
  /**
   * Copy for body
   */
  body: string | ReactNode
  /**
   * Copy and URL for button
   */
  button?: FiftyFiftyButton
  /**
   * Optional custom className for styling
   */
  className?: string
  /**
   * Optional brand identifier
   */
  brand?: string
}

export default function FiftyFifty({
  backgroundColor,
  image,
  videoUrl,
  layout,
  eyebrow,
  showEyebrow = true,
  header,
  body,
  button,
  subHeader,
  className,
  brand = '',
}: FiftyFiftyProps) {
  const videoId = videoUrl ? findYoutubeId(videoUrl) : null
  return (
    <PageSection
      className={clsx(
        backgroundColor === 'grey' ? 'bg-neutral-200' : 'bg-white',
        'px-6',
      )}
    >
      <>
        <div
          className={clsx(
            'py-10 px-6 md:flex md:justify-between md:items-center md:gap-11 lg:gap-20 md:py-[60px] md:px-10 lg:py-[72px]',
            layout === 'image-right'
              ? 'md:flex-row-reverse lg:pr-10 lg:pl-16 xl:pr-16 xl:pl-36'
              : 'lg:pl-10 lg:pr-16 xl:pl-16 xl:pr-36',
            className,
          )}
        >
          <div
            className={clsx(
              'mb-8 md:basis-1/2 lg:max-w-[672px] md:mb-0',
              videoId
                ? ''
                : image?.src === ''
                  ? 'w-full aspect-[4/3] bg-neutral-400 rounded-8 md:rounded-16 lg:aspect-[6/5]'
                  : '',
            )}
          >
            {videoId ? (
              <YouTubeFacade
                videoId={videoId}
                title={typeof header === 'string' ? header : 'Video'}
                className="rounded-8 md:rounded-16"
              />
            ) : (
              <Image
                className="rounded-8 md:rounded-16"
                src={image?.src || ''}
                alt={image?.alt ? image.alt : ''}
              />
            )}
          </div>
          <div className="md:basis-1/2 lg:max-w-[480px]">
            {showEyebrow &&
              (eyebrow ? (
                <div className="text-theme-accessibility flex gap-2 items-center flex-shrink-0 basis-1/2">
                  <Icon icon={eyebrow.icon.icon} className="h-6 w-6" />
                  <span className="uppercase text-[18px]">{eyebrow.text}</span>
                </div>
              ) : (
                <Icon
                  icon="ampersand"
                  className="h-[43px] w-[46px] text-theme-secondary"
                />
              ))}
            <Header
              as="h2"
              overrideClassName
              className="type-headline-2 mt-4 md:mt-2 md:type-headline-2 lg:mt-4 lg:type-display-3"
            >
              {header}
            </Header>
            {subHeader && (
              <div className="type-body-1 font-semibold">{subHeader}</div>
            )}
            <div className="type-body-6 mt-4 lg:type-body-4">{body}</div>
            {button && (
              <Button
                variant="secondary"
                className="mt-6 w-full md:w-auto text-xs type-body-7 md:text-[12px] lg:text-base no-underline"
                link={button.link}
                onClick={
                  button.action ? BUTTON_ACTIONS[button.action] : undefined
                }
              >
                {button.text}
              </Button>
            )}
          </div>
        </div>
        <>
          {header === 'Ship golf clubs' && <GolfBagSizes />}
          {header === 'Ship luggage' &&
            (brand === 'shipskis' ? <WinterLuggageSizes /> : <LuggageSizes />)}
          {header === 'Ship your winter gear' && <WinterGearSizes />}
        </>
      </>
    </PageSection>
  )
}
